import React, { FC, useState } from 'react';
import Auth, { CognitoUser } from '@aws-amplify/auth';
import { useTranslation } from 'react-i18next';
import { Box, Button, Link, Typography } from '@material-ui/core';
import { Formik, Form } from 'formik';

import { ForgotPassword, SignUp } from 'Links';
import { useNotify, useGlobalClient } from 'hooks';
import {
  Layouts,
  FormikEffect,
  VisibilityAdornment,
  ClientLogo,
  ValidatedTextFieldNext,
  Loaders,
} from 'components';
import { COGNITO_CHALLENGE_NAME_NEW_PASSWORD_REQUIRED } from 'features/auth/constants';
import { getAppImage } from 'helpers';

import { SignInSchema, ISignInValues } from '../validations';
import { AuthContainerProps } from '../types';
import RequireNewPassword from './RequireNewPassword';

const SignIn: FC<AuthContainerProps> = ({ authData, updateUsername, updatePassword }) => {
  const [isWorking, setIsWorking] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [tempCognitoUser, setTempCognitoUser] = useState<CognitoUser>(null);

  const client = useGlobalClient();
  const { t } = useTranslation();
  const notify = useNotify();

  const initialValues: ISignInValues = {
    username: authData.username,
    password: '',
  };

  function handleChange({ username, password }: ISignInValues) {
    updateUsername(username.toLowerCase());
    updatePassword(password);
  }

  async function onSubmit(values: ISignInValues) {
    setIsWorking(true);
    const sanitizedValues = { ...values, username: values.username.toLowerCase().trim() };

    try {
      const user = await Auth.signIn(sanitizedValues);
      if (user.challengeName === COGNITO_CHALLENGE_NAME_NEW_PASSWORD_REQUIRED) {
        setTempCognitoUser(user);
      }
    } catch (error) {
      setIsWorking(false);
      notify.error({ message: error.message });
    }
  }

  function renderImage() {
    return <img width={500} height={500} {...getAppImage('illustrations/boat.png', 500)} />;
  }

  function renderSidebar() {
    return (
      <Formik onSubmit={onSubmit} initialValues={initialValues} validationSchema={SignInSchema}>
        <Layouts.OnboardingBox component={Form}>
          <FormikEffect onChange={handleChange} delay={100} />
          <Box textAlign="center">
            <ClientLogo client={client} />
          </Box>
          <Typography align="center" paragraph variant="h2">
            {t('auth:signInButton')}
          </Typography>
          <Box paddingTop={3}>
            <ValidatedTextFieldNext
              enableTrim
              fullWidth
              name="username"
              type="email"
              label={t('auth:clientSelectLabel')}
              placeholder={t('auth:clientSelectPlaceholder')}
            />
          </Box>
          <Box paddingTop={3}>
            <ValidatedTextFieldNext
              enableTrim
              fullWidth
              name="password"
              type={passwordVisible ? 'text' : 'password'}
              label={t('user:password')}
              placeholder={t('user:password')}
              InputProps={{
                endAdornment: (
                  <VisibilityAdornment
                    visible={passwordVisible}
                    onToggle={() => setPasswordVisible(!passwordVisible)}
                  />
                ),
              }}
            />
          </Box>
          <Box paddingTop={3}>
            <Button
              fullWidth
              type="submit"
              size="large"
              variant="contained"
              color="primary"
              disabled={isWorking}
            >
              {isWorking ? <Loaders.Standard height={26} /> : t('auth:signInButton')}
            </Button>
          </Box>
          <Box paddingTop={1}>
            <Link color="textSecondary" component={ForgotPassword}>
              {t('auth:signInForgotPassword')}
            </Link>
          </Box>
          <Box paddingTop={3} textAlign="center">
            <Link color="textSecondary" component={SignUp}>
              {t('auth:signInSignUp')}
            </Link>
          </Box>
        </Layouts.OnboardingBox>
      </Formik>
    );
  }

  if (tempCognitoUser) {
    return <RequireNewPassword cognitoUser={tempCognitoUser} />;
  }

  return <Layouts.Onboarding image={renderImage()} main={renderSidebar()} />;
};

export default SignIn;
